// @ts-nocheck

import * as React from 'react';
import * as Yup from 'yup';
import { Field, Formik, FieldProps, FormikProps, FormikValues } from 'formik';
import { Mutation, Query } from 'react-apollo';
import CreateEatery from '../../graphql/mutations/CreateEatery';
import CreateShop from '../../graphql/mutations/CreateShop';
import CreateFacility from '../../graphql/mutations/CreateFacility';
import GetEatery from '../../graphql/queries/GetEatery';
import GetShop from '../../graphql/queries/GetShop';
import GetFacility from '../../graphql/queries/GetFacility';
import UpdateEatery from '../../graphql/mutations/UpdateEatery';
import UpdateShop from '../../graphql/mutations/UpdateShop';
import UpdateFacility from '../../graphql/mutations/UpdateFacility';
import GetPost from '../../graphql/queries/GetPost';
// import { omit } from 'lodash';
import {
  StyledForm,
  FieldRow,
  StyledFormGroup,
  StyledFormSelect,
  Card,
  Button,
  NewHeroImageCrop as NewHeroImage,
  StyledTextArea,
  Box,
} from '../../components';
import Span from '../../typography/Span';
import { formatGqlError } from '../../utils/helpers';
import { SelectObject, ImageObject } from '../../utils/types';
import { StakeholderRole, PostStatus } from '../../generated/graphql';
import { ApolloError } from 'apollo-client';
import { withRouter, RouteComponentProps } from 'react-router';
import { ValidateCharacterLimit } from './../../utils/helpers';
import GetAllShops from '../../graphql/queries/GetAllShops';
import GetAllFacilities from '../../graphql/queries/GetAllFacilities';
import GetAllEateries from '../../graphql/queries/GetAllEateries';
import GetAllPosts from '../../graphql/queries/GetAllPosts';
import PdfUploadListField from '../../components/PdfUploadListField';
// import NewFile from '../../components';

const STATUS_OPTIONS: SelectObject[] = [
  { label: 'Draft', value: PostStatus.Draft },
  { label: 'Published', value: PostStatus.Published },
  { label: 'Archived', value: PostStatus.Archived },
];

type ContentManagementFormValues = {
  name?: string;
  description?: string;
  telephoneNumber?: string;
  whatsappNumber?: string;
  email?: string;
  status?: string | SelectObject;
  id?: string;
  mondayHours?: string;
  tuesdayHours?: string;
  wednesdayHours?: string;
  thursdayHours?: string;
  fridayHours?: string;
  saturdayHours?: string;
  sundayHours?: string;
  menus?: string;
  menuName?: string;
  websiteUrl?: string;
  hyperlinks?: any[];
  pdfs?: string[];
};

interface Props extends RouteComponentProps {
  error?: ApolloError;
  buttonText?: string;
  loading: boolean;
  isNewPost?: boolean;
  initialValues: FormikValues;
  roles?: StakeholderRole;
  image?: ImageObject;
  step?: string;
  formType?: string;
}

const ContentManagementForm: React.FC<Props> = ({
  initialValues,
  error,
  loading,
  isNewPost,
  image,
  history,
  ...rest
}) => {
  // Add state for managing uploaded image
  const [uploadedImage, setUploadedImage] = React.useState<any | null>(null);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    telephoneNumber: Yup.string(),
    whatsappNumber: Yup.string(),
    email: Yup.string().email('Invalid email format'),
    websiteUrl: Yup.string()
      .trim()
      .matches(
        /^(https?:\/\/)[^\s]+$/i,
        'Website URL must be a valid http(s) URL',
      )
      .nullable(true)
      .notRequired(),
    status: Yup.string().default('DRAFT'),
    description: Yup.string().required('Required'),
    mondayHours: Yup.string().required('Required').max(15, 'Maximum 15 characters'),
    tuesdayHours: Yup.string().required('Required').max(15, 'Maximum 15 characters'),
    wednesdayHours: Yup.string().required('Required').max(15, 'Maximum 15 characters'),
    thursdayHours: Yup.string().required('Required').max(15, 'Maximum 15 characters'),
    fridayHours: Yup.string().required('Required').max(15, 'Maximum 15 characters'),
    saturdayHours: Yup.string().required('Required').max(15, 'Maximum 15 characters'),
    sundayHours: Yup.string().required('Required').max(15, 'Maximum 15 characters'),
    menuName: Yup.string().when('step', {
      is: 'dining',
      then: Yup.string(),
      otherwise: Yup.string(),
    }),
  });

  const renderPostImage = (): any => {
    if (
      !history.location.pathname.includes('content-management/operational-notices') &&
      initialValues.type !== 'UPDATE'
    ) {
      if (isNewPost) {
        return (
          <div>
            <NewHeroImage
              className="ContentManagementAddImage"
              uploadText="ADD IMAGE"
              onUploadComplete={(data) => {
                // Handle the uploaded image data
                if (!data) {
                  return;
                }
                // @ts-ignore
                setUploadedImage(data);
              }}
              allowedFileTypes={['image/*']}
              modalHeading="Upload Image"
            />
          </div>
        );
      }
      if (!uploadedImage && initialValues && initialValues.logo) {
        setUploadedImage(initialValues && initialValues.logo);
      }

      return (
        <NewHeroImage
          className="ContentManagementAddImage"
          uploadText="ADD IMAGE"
          src={initialValues && initialValues.logo && (initialValues.logo || initialValues.logo)}
          onUploadComplete={(data: ImageObject | null) => {
            if (!data) {
              return;
            }
            setUploadedImage({
              original: data.original || data.scaled,
              scaled: data.scaled,
              thumbnail: data.thumbnail,
            });
          }}
          allowedFileTypes={['image/*']}
          modalHeading="Upload Image"
        />
      );
    }
    return null;
  };

  const defaultValues = {
    name: '',
    telephoneNumber: '',
    whatsappNumber: '',
    email: '',
    status: STATUS_OPTIONS[0],
    description: '',
    mondayHours: '',
    tuesdayHours: '',
    wednesdayHours: '',
    thursdayHours: '',
    fridayHours: '',
    saturdayHours: '',
    sundayHours: '',
    menuName: '',
    menus: '',
    websiteUrl: '',
    hyperlinks: [],
    pdfs: [],
  };

  const handleSubmit = (values: any, { setSubmitting }: any) => {
    setSubmitting(true);

    // Build hyperlinks for facilities, preserving existing non-Website links
    let hyperlinks = Array.isArray(values.hyperlinks) ? values.hyperlinks : [];

    const steps = ['facilities', 'dining', 'services', 'shopping'];
    if (rest.step && steps.includes(rest.step)) {
      const websiteUrl = values.websiteUrl;

      const hyperlinksWithoutWebsite = hyperlinks.filter(
        (h: any) => h && h.buttonText !== 'Website',
      );

      hyperlinks =
        websiteUrl && websiteUrl.trim().length > 0
          ? [
              ...hyperlinksWithoutWebsite,
              {
                icon: {
                  name: 'web',
                  family: 'MaterialCommunityIcons',
                },
                link: websiteUrl.trim(),
                buttonText: 'Website',
              },
            ]
          : hyperlinksWithoutWebsite;
    }

    const commonVariables = {
      name: values.name,
      status: values.status.value || 'DRAFT',
      description: values.description,
      email: values.email,
      telephoneNumber: values.telephoneNumber,
      whatsappNumber: values.whatsappNumber,
      mondayHours: values.mondayHours,
      tuesdayHours: values.tuesdayHours,
      wednesdayHours: values.wednesdayHours,
      thursdayHours: values.thursdayHours,
      fridayHours: values.fridayHours,
      saturdayHours: values.saturdayHours,
      sundayHours: values.sundayHours,
      tradingHours: values.tradingHours,
      menus: Array.isArray(values.menus)
        ? JSON.stringify(values.menus)
        : typeof values.menus === 'string'
        ? values.menus
        : JSON.stringify([]),
      pdfs: Array.isArray(values.pdfs)
        ? JSON.stringify(values.pdfs)
        : typeof values.pdfs === 'string'
        ? values.pdfs
        : JSON.stringify([]),
      logo: uploadedImage ? uploadedImage.original : image ? image.original : null,
      ...(rest.step && steps.includes(rest.step) ? { hyperlinks } : {}),
    };

    // Handle create operations
    if (rest.formType === 'create') {
      console.log('Create operation variables:', { data: commonVariables });
      switch (rest.step) {
        case 'shopping':
          return {
            variables: {
              data: {
                ...commonVariables,
                isService: false,
              },
            },
          };
        case 'services':
          return {
            variables: {
              data: {
                ...commonVariables,
                isService: true,
              },
            },
          };
        case 'facilities':
        case 'dining':
          return {
            variables: {
              data: commonVariables,
            },
          };
        default:
          break;
      }
    }

    // Handle update operations
    if (rest.formType === 'edit' && initialValues.id) {
      return {
        variables: {
          id: initialValues.id,
          data: commonVariables,
        },
      };
    }

    setSubmitting(false);
    return null;
  };

  const getMutation = () => {
    if (rest.formType === 'create') {
      switch (rest.step) {
        case 'shopping':
        case 'services':
          return CreateShop;
        case 'facilities':
          return CreateFacility;
        case 'dining':
          return CreateEatery;
        default:
          return CreateEatery;
      }
    } else {
      // Edit mutations
      switch (rest.step) {
        case 'shopping':
        case 'services':
          return UpdateShop;
        case 'facilities':
          return UpdateFacility;
        case 'dining':
          return UpdateEatery;
        default:
          return UpdateEatery;
      }
    }
  };

  const getQuery = () => {
    switch (rest.step) {
      case 'shopping':
      case 'services':
        return GetShop;
      case 'facilities':
        return GetFacility;
      case 'dining':
        return GetEatery;
      default:
        return GetEatery;
    }
  };

  const mutation = getMutation();
  const query = getQuery();

  // Fixed redirectPath to correctly go back to the content management table
  // The content management table routes all follow the pattern /content-management/{step}
  const redirectPath = `/content-management/${rest.step}`;
  console.log('Setting redirectPath for step:', rest.step, redirectPath);

  if (!isNewPost && rest.formType === 'edit' && initialValues.id) {
    console.log('Fetching data with ID:', initialValues.id);
    return (
      <Query query={query} variables={{ id: initialValues.id }}>
        {({ loading: queryLoading, error: queryError, data }) => {
          console.log('Query Response:', { loading: queryLoading, error: queryError, data });

          let entityData = {};
          if (data) {
            switch (rest.step) {
              case 'shopping':
              case 'services':
                entityData = data.shop || {};
                break;
              case 'facilities':
                entityData = data.facility || {};
                break;
              case 'dining':
                entityData = data.eatery || {};
                break;
              default:
                entityData = {};
            }
          }

          const websiteHyperlink =
            Array.isArray((entityData as any).hyperlinks) &&
            (entityData as any).hyperlinks.find((h: any) => h && h.buttonText === 'Website');

          const formInitialValues = {
            ...defaultValues,
            ...entityData,
            // @ts-ignore
            status: STATUS_OPTIONS.find((option) => option.value === entityData.status),
            id: initialValues.id,
            websiteUrl: websiteHyperlink ? websiteHyperlink.link : '',
          };

          return (
            <Mutation
              mutation={mutation}
              onCompleted={() => {
                console.log('Edit form completed, redirecting to:', redirectPath);
                history.push(redirectPath);
              }}
              refetchQueries={() => {
                switch (rest.step) {
                  case 'shopping':
                  case 'services':
                    return [
                      { query: GetAllShops },
                      ...(initialValues.id ? [{ query: GetShop, variables: { id: initialValues.id } }] : []),
                    ];
                  case 'facilities':
                    return [
                      { query: GetAllFacilities },
                      ...(initialValues.id ? [{ query: GetFacility, variables: { id: initialValues.id } }] : []),
                    ];
                  case 'dining':
                    return [
                      { query: GetAllEateries },
                      ...(initialValues.id ? [{ query: GetEatery, variables: { id: initialValues.id } }] : []),
                    ];
                  case 'notifications':
                  case 'operational-notices':
                    return [
                      { query: GetAllPosts },
                      ...(initialValues.id ? [{ query: GetPost, variables: { id: initialValues.id } }] : []),
                    ];
                  default:
                    return [];
                }
              }}
              awaitRefetchQueries={true}
            >
              {(updateEntity, { loading: mutationLoading }) => (
                <Formik
                  validationSchema={validationSchema}
                  enableReinitialize={true}
                  initialValues={{
                    ...formInitialValues,
                    pdfs: Array.isArray((entityData as any).pdfs)
                      ? JSON.stringify((entityData as any).pdfs)
                      : (entityData as any).pdfs || '',
                    menus: Array.isArray((entityData as any).menus)
                      ? JSON.stringify((entityData as any).menus)
                      : (entityData as any).menus || '',
                  }}
                  onSubmit={(values, formikBag) => {
                    console.log('Update Form Values:', values);
                    console.log('Update Form Menus:', values.menus);
                    const mutationVariables = handleSubmit(values, formikBag);
                    console.log('Update Mutation Variables:', mutationVariables);
                    if (mutationVariables) {
                      updateEntity({
                        variables: {
                          id: initialValues.id,
                          ...mutationVariables.variables,
                        },
                      });
                    }
                  }}
                  render={(formikProps: FormikProps<ContentManagementFormValues>): any => {
                    return (
                      <StyledForm flexDirection="row">
                        <Card flex={3} bg="white" p={4}>
                          <FieldRow>
                            <Field
                              name="name"
                              render={({ field }: FieldProps<ContentManagementFormValues>): any => (
                                <StyledFormGroup
                                  {...field}
                                  field={field}
                                  form={formikProps}
                                  label="Name"
                                  placeholder="Enter company name"
                                />
                              )}
                            />
                          </FieldRow>
                          <FieldRow>
                            <Field
                              name="description"
                              validate={ValidateCharacterLimit}
                              render={({ field }: FieldProps<ContentManagementFormValues>): any => (
                                <StyledTextArea
                                  {...field}
                                  field={field}
                                  form={formikProps}
                                  label="Description"
                                  placeholder="Enter company description"
                                />
                              )}
                            />
                          </FieldRow>
                          <FieldRow>
                            <Box width={1 / 3} pr={2}>
                              <Field
                                name="telephoneNumber"
                                render={({ field }: FieldProps<ContentManagementFormValues>): any => (
                                  <StyledFormGroup
                                    {...field}
                                    field={field}
                                    form={formikProps}
                                    label="Telephone Number"
                                    placeholder="eg 074 712 ****"
                                  />
                                )}
                              />
                            </Box>
                            <Box width={1 / 3} px={2}>
                              <Field
                                name="whatsappNumber"
                                render={({ field }: FieldProps<ContentManagementFormValues>): any => (
                                  <StyledFormGroup
                                    {...field}
                                    field={field}
                                    form={formikProps}
                                    label="Whatsapp Number"
                                    placeholder="eg 082 123 ****"
                                  />
                                )}
                              />
                            </Box>
                            <Box width={1 / 3} pl={2}>
                              <Field
                                name="email"
                                render={({ field }: FieldProps<ContentManagementFormValues>): any => (
                                  <StyledFormGroup
                                    {...field}
                                    field={field}
                                    form={formikProps}
                                    label="Email"
                                    placeholder="Enter email address"
                                  />
                                )}
                              />
                            </Box>
                          </FieldRow>
                          <FieldRow>
                            <Box width={1 / 3} pr={2}>
                              <Field
                                name="websiteUrl"
                                render={({ field }: FieldProps<ContentManagementFormValues>): any => (
                                  <StyledFormGroup
                                    {...field}
                                    field={field}
                                    form={formikProps}
                                    label="Website URL"
                                    placeholder="https://example.com"
                                  />
                                )}
                              />
                            </Box>
                          </FieldRow>
                          <FieldRow>
                            <Box width={1 / 3} pr={2}>
                              <Field
                                name="mondayHours"
                                render={({ field }: FieldProps<ContentManagementFormValues>): any => (
                                  <StyledFormGroup
                                    {...field}
                                    field={field}
                                    form={formikProps}
                                    label="Monday Hours"
                                    placeholder="e.g. 09:00 - 17:00"
                                    maxLength={15}
                                  />
                                )}
                              />
                            </Box>
                            <Box width={1 / 3} px={2}>
                              <Field
                                name="tuesdayHours"
                                render={({ field }: FieldProps<ContentManagementFormValues>): any => (
                                  <StyledFormGroup
                                    {...field}
                                    field={field}
                                    form={formikProps}
                                    label="Tuesday Hours"
                                    placeholder="e.g. 09:00 - 17:00"
                                    maxLength={15}
                                  />
                                )}
                              />
                            </Box>
                            <Box width={1 / 3} pl={2}>
                              <Field
                                name="wednesdayHours"
                                render={({ field }: FieldProps<ContentManagementFormValues>): any => (
                                  <StyledFormGroup
                                    {...field}
                                    field={field}
                                    form={formikProps}
                                    label="Wednesday Hours"
                                    placeholder="e.g. 09:00 - 17:00"
                                    maxLength={15}
                                  />
                                )}
                              />
                            </Box>
                          </FieldRow>
                          <FieldRow>
                            <Box width={1 / 3} pr={2}>
                              <Field
                                name="thursdayHours"
                                render={({ field }: FieldProps<ContentManagementFormValues>): any => (
                                  <StyledFormGroup
                                    {...field}
                                    field={field}
                                    form={formikProps}
                                    label="Thursday Hours"
                                    placeholder="e.g. 09:00 - 17:00"
                                    maxLength={15}
                                  />
                                )}
                              />
                            </Box>
                            <Box width={1 / 3} px={2}>
                              <Field
                                name="fridayHours"
                                render={({ field }: FieldProps<ContentManagementFormValues>): any => (
                                  <StyledFormGroup
                                    {...field}
                                    field={field}
                                    form={formikProps}
                                    label="Friday Hours"
                                    placeholder="e.g. 09:00 - 17:00"
                                    maxLength={15}
                                  />
                                )}
                              />
                            </Box>
                            <Box width={1 / 3} pl={2}>
                              <Field
                                name="saturdayHours"
                                render={({ field }: FieldProps<ContentManagementFormValues>): any => (
                                  <StyledFormGroup
                                    {...field}
                                    field={field}
                                    form={formikProps}
                                    label="Saturday Hours"
                                    placeholder="e.g. 09:00 - 17:00"
                                    maxLength={15}
                                  />
                                )}
                              />
                            </Box>
                          </FieldRow>
                          <FieldRow>
                            <Box width={1 / 3} pr={2}>
                              <Field
                                name="sundayHours"
                                render={({ field }: FieldProps<ContentManagementFormValues>): any => (
                                  <StyledFormGroup
                                    {...field}
                                    field={field}
                                    form={formikProps}
                                    label="Sunday Hours"
                                    placeholder="e.g. 09:00 - 17:00"
                                    maxLength={15}
                                  />
                                )}
                              />
                            </Box>
                          </FieldRow>
                        </Card>
                        <Card ml={[0, 0, 4]} flex={1} bg="white" p={4}>
                          {renderPostImage()}
                          <PdfUploadListField
                            formikProps={formikProps}
                            pdfsField="pdfs"
                            pdfNameField="linkName"
                          />
                          <Field
                            name="status"
                            render={({ field }: FieldProps<ContentManagementFormValues>): any => (
                              <StyledFormSelect
                                {...field}
                                field={field}
                                label="Status"
                                form={formikProps}
                                options={STATUS_OPTIONS}
                              />
                            )}
                          />
                          <Field
                            name="id"
                            render={({ field }: FieldProps<ContentManagementFormValues>): any => (
                              <StyledFormGroup
                                {...field}
                                field={field}
                                form={formikProps}
                                label="Unique ID"
                                placeholder="Unique ID"
                                disabled={true}
                              />
                            )}
                          />
                          {error && (
                            <Span width="100%" textAlign="center" color="error" mb={3}>
                              {formatGqlError(error)}
                            </Span>
                          )}
                          <Button mb={1} type="submit" text="SAVE & UPDATE" isLoading={mutationLoading} />
                        </Card>
                      </StyledForm>
                    );
                  }}
                />
              )}
            </Mutation>
          );
        }}
      </Query>
    );
  }

  return (
    <Mutation
      mutation={mutation}
      onCompleted={() => {
        console.log('Create form completed, redirecting to:', redirectPath);
        history.push(redirectPath);
      }}
      refetchQueries={() => {
        switch (rest.step) {
          case 'shopping':
          case 'services':
            return [
              { query: GetAllShops },
            ];
          case 'facilities':
            return [
              { query: GetAllFacilities },
            ];
          case 'dining':
            return [
              { query: GetAllEateries },
            ];
          case 'notifications':
          case 'operational-notices':
            return [
              { query: GetAllPosts },
            ];
          default:
            return [];
        }
      }}
      awaitRefetchQueries={true}
    >
      {(createEntity, { loading: mutationLoading }) => (
        <Formik
          validationSchema={validationSchema}
          enableReinitialize={true}
          initialValues={{
            ...defaultValues,
            ...initialValues,
            websiteUrl:
              Array.isArray((initialValues as any).hyperlinks) &&
              (initialValues as any).hyperlinks.find((h: any) => h && h.buttonText === 'Website')
                ? (initialValues as any).hyperlinks.find(
                    (h: any) => h && h.buttonText === 'Website',
                  ).link
                : '',
            pdfs: Array.isArray((initialValues as any).pdfs)
              ? JSON.stringify((initialValues as any).pdfs)
              : (initialValues as any).pdfs || '',
            menus: Array.isArray(initialValues.menus) ? JSON.stringify(initialValues.menus) : initialValues.menus || '',
          }}
          onSubmit={(values, formikBag) => {
            console.log('Create Form Values:', values);
            console.log('Create Form Menus:', values.menus);
            const mutationVariables = handleSubmit(values, formikBag);
            console.log('Create Mutation Variables:', mutationVariables);
            if (mutationVariables) {
              createEntity(mutationVariables);
            }
          }}
          render={(formikProps: FormikProps<ContentManagementFormValues>): any => {
            return (
              <StyledForm flexDirection="row">
                <Card flex={3} bg="white" p={4}>
                  <FieldRow>
                    <Field
                      name="name"
                      render={({ field }: FieldProps<ContentManagementFormValues>): any => (
                        <StyledFormGroup
                          {...field}
                          field={field}
                          form={formikProps}
                          label="Name"
                          placeholder="Enter company name"
                        />
                      )}
                    />
                  </FieldRow>
                  <FieldRow>
                    <Field
                      name="description"
                      validate={ValidateCharacterLimit}
                      render={({ field }: FieldProps<ContentManagementFormValues>): any => (
                        <StyledTextArea
                          {...field}
                          field={field}
                          form={formikProps}
                          label="Description"
                          placeholder="Enter company description"
                        />
                      )}
                    />
                  </FieldRow>
                  <FieldRow>
                    <Box width={1 / 3} pr={2}>
                      <Field
                        name="telephoneNumber"
                        render={({ field }: FieldProps<ContentManagementFormValues>): any => (
                          <StyledFormGroup
                            {...field}
                            field={field}
                            form={formikProps}
                            label="Telephone Number"
                            placeholder="eg 074 712 ****"
                          />
                        )}
                      />
                    </Box>
                    <Box width={1 / 3} px={2}>
                      <Field
                        name="whatsappNumber"
                        render={({ field }: FieldProps<ContentManagementFormValues>): any => (
                          <StyledFormGroup
                            {...field}
                            field={field}
                            form={formikProps}
                            label="Whatsapp Number"
                            placeholder="eg 082 123 ****"
                          />
                        )}
                      />
                    </Box>
                    <Box width={1 / 3} pl={2}>
                      <Field
                        name="email"
                        render={({ field }: FieldProps<ContentManagementFormValues>): any => (
                          <StyledFormGroup
                            {...field}
                            field={field}
                            form={formikProps}
                            label="Email"
                            placeholder="Enter email address"
                          />
                        )}
                      />
                    </Box>
                  </FieldRow>
                <FieldRow>
                  <Box width={1 / 3} pr={2}>
                    <Field
                      name="websiteUrl"
                      render={({ field }: FieldProps<ContentManagementFormValues>): any => (
                        <StyledFormGroup
                          {...field}
                          field={field}
                          form={formikProps}
                          label="Website URL"
                          placeholder="https://example.com"
                        />
                      )}
                    />
                  </Box>
                </FieldRow>
                  <FieldRow>
                    <Box width={1 / 3} pr={2}>
                      <Field
                        name="mondayHours"
                        render={({ field }: FieldProps<ContentManagementFormValues>): any => (
                          <StyledFormGroup
                            {...field}
                            field={field}
                            form={formikProps}
                            label="Monday Hours"
                            placeholder="e.g. 09:00 - 17:00"
                            maxLength={15}
                          />
                        )}
                      />
                    </Box>
                    <Box width={1 / 3} px={2}>
                      <Field
                        name="tuesdayHours"
                        render={({ field }: FieldProps<ContentManagementFormValues>): any => (
                          <StyledFormGroup
                            {...field}
                            field={field}
                            form={formikProps}
                            label="Tuesday Hours"
                            placeholder="e.g. 09:00 - 17:00"
                            maxLength={15}
                          />
                        )}
                      />
                    </Box>
                    <Box width={1 / 3} pl={2}>
                      <Field
                        name="wednesdayHours"
                        render={({ field }: FieldProps<ContentManagementFormValues>): any => (
                          <StyledFormGroup
                            {...field}
                            field={field}
                            form={formikProps}
                            label="Wednesday Hours"
                            placeholder="e.g. 09:00 - 17:00"
                            maxLength={15}
                          />
                        )}
                      />
                    </Box>
                  </FieldRow>
                  <FieldRow>
                    <Box width={1 / 3} pr={2}>
                      <Field
                        name="thursdayHours"
                        render={({ field }: FieldProps<ContentManagementFormValues>): any => (
                          <StyledFormGroup
                            {...field}
                            field={field}
                            form={formikProps}
                            label="Thursday Hours"
                            placeholder="e.g. 09:00 - 17:00"
                            maxLength={15}
                          />
                        )}
                      />
                    </Box>
                    <Box width={1 / 3} px={2}>
                      <Field
                        name="fridayHours"
                        render={({ field }: FieldProps<ContentManagementFormValues>): any => (
                          <StyledFormGroup
                            {...field}
                            field={field}
                            form={formikProps}
                            label="Friday Hours"
                            placeholder="e.g. 09:00 - 17:00"
                            maxLength={15}
                          />
                        )}
                      />
                    </Box>
                    <Box width={1 / 3} pl={2}>
                      <Field
                        name="saturdayHours"
                        render={({ field }: FieldProps<ContentManagementFormValues>): any => (
                          <StyledFormGroup
                            {...field}
                            field={field}
                            form={formikProps}
                            label="Saturday Hours"
                            placeholder="e.g. 09:00 - 17:00"
                            maxLength={15}
                          />
                        )}
                      />
                    </Box>
                  </FieldRow>
                  <FieldRow>
                    <Box width={1 / 3} pr={2}>
                      <Field
                        name="sundayHours"
                        render={({ field }: FieldProps<ContentManagementFormValues>): any => (
                          <StyledFormGroup
                            {...field}
                            field={field}
                            form={formikProps}
                            label="Sunday Hours"
                            placeholder="e.g. 09:00 - 17:00"
                            maxLength={15}
                          />
                        )}
                      />
                    </Box>
                  </FieldRow>
                </Card>
                <Card ml={[0, 0, 4]} flex={1} bg="white" p={4}>
                  {renderPostImage()}
                  <PdfUploadListField
                    formikProps={formikProps}
                    pdfsField="pdfs"
                    pdfNameField="linkName"
                  />

                  <Field
                    name="status"
                    render={({ field }: FieldProps<ContentManagementFormValues>): any => (
                      <StyledFormSelect
                        {...field}
                        field={field}
                        label="Status"
                        form={formikProps}
                        options={STATUS_OPTIONS}
                      />
                    )}
                  />
                  <Field
                    name="id"
                    render={({ field }: FieldProps<ContentManagementFormValues>): any => (
                      <StyledFormGroup
                        {...field}
                        field={field}
                        form={formikProps}
                        label="Unique ID"
                        placeholder="Unique ID"
                        disabled={true}
                      />
                    )}
                  />
                  {error && (
                    <Span width="100%" textAlign="center" color="error" mb={3}>
                      {formatGqlError(error)}
                    </Span>
                  )}
                  <Button mb={1} type="submit" text="SAVE & UPDATE" isLoading={mutationLoading} />
                </Card>
              </StyledForm>
            );
          }}
        />
      )}
    </Mutation>
  );
};

export default withRouter(ContentManagementForm);



// WEBPACK FOOTER //
// ./src/forms/contentManagement/ContentManagementForm.tsx